import React, { useState, useCallback, useEffect } from 'react';
import { Group, Text, RangeSlider } from '@mantine/core';

const theme = {
  background: { secondary: '#161B22', tertiary: '#21262D', elevated: '#2D333B' },
  accent: { primary: '#4F46E5' },
  border: { primary: '#374151' },
  text: { primary: '#F9FAFB', secondary: '#9CA3AF', disabled: '#6B7280' }
};

const formatSecondsToReadable = (seconds?: number): string => {
  if (seconds === undefined || seconds === 0) return '0s';
  if (seconds === 31536001) return 'Max';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  let result = '';
  if (days > 0) result += `${days}d`;
  if (hours > 0) result += `${hours}h`;
  if (minutes > 0) result += `${minutes}m`;
  if (remainingSeconds > 0 || result === '') result += `${remainingSeconds}s`;
  return result;
};

const parseReadableToSeconds = (input: string): number => {
  if (!input || input === '0s') return 0;
  let total = 0;
  const dayMatch = input.match(/(\d+)d/);
  const hourMatch = input.match(/(\d+)h/);
  const minuteMatch = input.match(/(\d+)m/);
  const secondMatch = input.match(/(\d+)s/);
  if (dayMatch) total += parseInt(dayMatch[1]) * 86400;
  if (hourMatch) total += parseInt(hourMatch[1]) * 3600;
  if (minuteMatch) total += parseInt(minuteMatch[1]) * 60;
  if (secondMatch) total += parseInt(secondMatch[1]);
  return total;
};

const generateTimeScale = (): number[] => {
  const scale = [0];
  for (let i = 10; i <= 60; i += 10) scale.push(i);
  scale.push(120, 300, 600, 900, 1800, 3600);
  scale.push(7200, 14400, 21600, 43200, 86400);
  scale.push(172800, 259200, 432000, 604800);
  scale.push(1209600, 1814400, 2419200);
  scale.push(5184000, 7776000, 15552000, 31536000);
  scale.push(31536001); // Represents "any duration"
  return scale;
};

const timeScale = generateTimeScale();

// Добавьте inputStyles и fillColor в пропсы
interface DurationRangeSliderProps {
  minValue?: number;
  maxValue?: number;
  onChange: (min: number | undefined, max: number | undefined) => void;
  inputStyles?: any;
  fillColor?: string; // NEW: color for slider fill
}

const DurationRangeSlider: React.FC<DurationRangeSliderProps> = ({
  minValue,
  maxValue,
  onChange,
  inputStyles,
  fillColor = '#10A981'
}) => {
  // Internal state for slider
  const [rangeValue, setRangeValue] = useState<[number, number]>([0, timeScale.length - 1]);
  const [isSliderDragging, setIsSliderDragging] = useState(false);

  // Helper: find closest index in timeScale for a given seconds value
  const findClosestIndex = (seconds?: number) => {
    if (seconds === undefined) return 0;
    let closest = 0;
    let minDiff = Math.abs(timeScale[0] - seconds);
    for (let i = 1; i < timeScale.length; i++) {
      const diff = Math.abs(timeScale[i] - seconds);
      if (diff < minDiff) {
        minDiff = diff;
        closest = i;
      }
    }
    return closest;
  };

  // Sync slider with props
  useEffect(() => {
    // Only update rangeValue if not dragging and the value is actually different
    if (!isSliderDragging) {
      const minIdx = findClosestIndex(minValue);
      const maxIdx = findClosestIndex(maxValue);
      // Only update if changed to avoid unnecessary resets
      if (rangeValue[0] !== minIdx || rangeValue[1] !== maxIdx) {
        setRangeValue([minIdx, maxIdx]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minValue, maxValue, isSliderDragging]);

  // When slider moves, update local state only and call onChange
  const handleRangeChange = useCallback((value: [number, number]) => {
    setIsSliderDragging(true);
    setRangeValue(value);
    const [minIdx, maxIdx] = value;
    const minSeconds = timeScale[minIdx];
    const maxSeconds = timeScale[maxIdx];
    onChange(minSeconds, maxSeconds);
  }, [onChange]);

  // When slider released, propagate up and update local state
  const handleRangeChangeEnd = useCallback((value: [number, number]) => {
    setIsSliderDragging(false);
    // Do not call setRangeValue here, let useEffect handle it from parent state
    const [minIdx, maxIdx] = value;
    const minSeconds = timeScale[minIdx];
    const maxSeconds = timeScale[maxIdx];
    // Only propagate up if values actually changed
    if (
      minValue !== minSeconds ||
      maxValue !== maxSeconds
    ) {
      onChange(minSeconds, maxSeconds);
    }
  }, [onChange, minValue, maxValue]);

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ padding: '16px 8px' }}>
        <Text size="xs" c={theme.text.secondary} mb="sm">Duration Range</Text>
        <RangeSlider
          value={rangeValue}
          onChange={handleRangeChange}
          // onChangeEnd={handleRangeChangeEnd}
          min={0}
          max={timeScale.length - 1}
          minRange={1}
          step={1}
          size="sm"
          color={theme.accent.primary}
          thumbSize={18}
          label={(value) => formatSecondsToReadable(timeScale[value])}
          labelAlwaysOn
          styles={{
            track: { backgroundColor: theme.background.elevated, height: 16},
            bar: { background: fillColor, height: 16 }, // GREEN fill
            thumb: { border: `2px solid ${fillColor}`, background: '#fff', width: 18, height: 18 },
            ...inputStyles
          }}
        />
      </div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        fontSize: '10px', 
        color: theme.text.disabled,
        marginTop: '8px'
      }}>
        <span>0s</span>
        <span>1min</span>
        <span>1h</span>
        <span>1d</span>
        <span>1w</span>
        <span>1m</span>
        <span>1y</span>
      </div>
    </div>
  );
};

export default DurationRangeSlider;
